import { useState, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import SkeletonTable from "components/SkeletonTable";
import { useAuthz } from "context/AuthzContext";
import { useConfirm } from "context/ConfirmContext";
import { PERMISSIONS } from "auth/permissions";
import { TC } from "../tokens/text";
import {
  listIndividualClients,
  createIndividualClient,
  archiveIndividualClient,
  listTenantUsers,
  extractIndividualIdDocument,
  createClientProfile,
  lookupCompaniesHouse,
  resyncClientFromCH,
} from "services/tenantUserAdminService";

// ─── Constants ────────────────────────────────────────────────────────────────

const RESIDENCY_OPTIONS = [
  { value: "uk_resident",       label: "UK Resident" },
  { value: "non_uk_resident",   label: "Non-UK Resident" },
  { value: "dual_resident",     label: "Dual Resident" },
  { value: "non_domiciled",     label: "Non-Domiciled" },
];

const EMPLOYMENT_TYPES = [
  { value: "employed",      label: "Employed" },
  { value: "self_employed", label: "Self-Employed" },
  { value: "director",      label: "Director" },
  { value: "retired",       label: "Retired" },
  { value: "none",          label: "None / Other" },
];

const ONBOARDING_STATUS = [
  { value: "pending",  label: "Pending" },
  { value: "partial",  label: "Partial" },
  { value: "complete", label: "Complete" },
];

const ACCESS_LEVELS = [
  { value: "partner",   label: "Partner" },
  { value: "senior",    label: "Senior Accountant" },
  { value: "reviewer",  label: "Reviewer" },
  { value: "read_only", label: "Read Only" },
];

const EMPTY_FORM = {
  // personal
  fullLegalName: "", preferredName: "", dateOfBirth: "",
  niNumber: "", utr: "", nationality: "", residencyStatus: "uk_resident",
  addressLine1: "", addressLine2: "", city: "", postcode: "", country: "United Kingdom",
  email: "", mobile: "", landline: "",
  // relationship
  assignedAccountantUid: "", accessLevel: "senior",
  onboardingStatus: "pending",
  // tax
  employmentType: "employed",
};

const DIALOG_SX = {
  backgroundColor: "#ffffff",
  color: TC.primary,
  border: "1px solid #d8e6f7",
  borderRadius: "16px",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function labelFor(list, val) {
  return list.find((i) => i.value === val)?.label || val || "—";
}

function maskNI(ni) {
  if (!ni) return "—";
  const clean = String(ni).replace(/\s/g, "");
  if (clean.length < 4) return "••••";
  return `${clean.slice(0, 2)}••••${clean.slice(-1)}`;
}

function StatusBadge({ status }) {
  const cfg = {
    active:   { label: "Active",   color: "#4caf50" },
    inactive: { label: "Inactive", color: "#ff9800" },
    archived: { label: "Archived", color: "#9e9e9e" },
  }[status] || { label: status, color: "#9e9e9e" };
  return (
    <Chip
      label={cfg.label.toUpperCase()}
      size="small"
      sx={{
        height: 20, fontSize: "0.62rem", fontWeight: 700,
        bgcolor: `${cfg.color}22`, color: cfg.color,
        border: `1px solid ${cfg.color}`,
      }}
    />
  );
}

function OnboardingChip({ status }) {
  const cfg = {
    pending:  { color: "#ff9800" },
    partial:  { color: "#2196f3" },
    complete: { color: "#4caf50" },
  }[status] || { color: "#9e9e9e" };
  return (
    <Chip
      label={(status || "pending").toUpperCase()}
      size="small"
      sx={{
        height: 18, fontSize: "0.6rem", fontWeight: 700,
        bgcolor: `${cfg.color}22`, color: cfg.color,
        border: `1px solid ${cfg.color}`,
      }}
    />
  );
}

function StyledSelect({ value, onChange, children, placeholder }) {
  return (
    <FormControl fullWidth size="small">
      <Select
        value={value}
        onChange={onChange}
        displayEmpty
        sx={{
          color: TC.primary, fontSize: "0.875rem",
          background: "#f4f8ff",
          border: "1px solid #d8e6f7",
          borderRadius: "8px",
          "& .MuiSelect-icon": { color: TC.secondary },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover": { background: "#f4f8ff" },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              background: "rgba(15,18,59,0.98)",
              border: "1px solid #d8e6f7",
              color: TC.primary,
              "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
              "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.25)" },
            },
          },
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ color: TC.secondary, fontSize: "0.875rem" }}>
            {placeholder}
          </MenuItem>
        )}
        {children}
      </Select>
    </FormControl>
  );
}

function FieldLabel({ children }) {
  return (
    <VuiTypography
      variant="caption" fontWeight="bold"
      sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", mt: 1.5, display: "block" }}
    >
      {children}
    </VuiTypography>
  );
}

// ─── Create / Edit Dialog ─────────────────────────────────────────────────────

function CreateDialog({ open, onClose, onSave, tenantId, users = [] }) {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null); // banner info
  const [error, setError]         = useState("");
  const fileInputRef              = useRef(null);

  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setError(""); setExtracted(null); }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ── ID scan ────────────────────────────────────────────────────────────────
  async function handleIdUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // reset so same file can be re-uploaded if needed
    e.target.value = "";

    setExtracting(true);
    setError("");
    setExtracted(null);
    try {
      const { extracted: data } = await extractIndividualIdDocument(tenantId, file);

      // Map extracted fields → form fields (only overwrite if non-null)
      setForm((f) => ({
        ...f,
        fullLegalName:   data.fullLegalName   || f.fullLegalName,
        dateOfBirth:     data.dateOfBirth     || f.dateOfBirth,
        nationality:     data.nationality     || f.nationality,
        niNumber:        data.niNumber        || f.niNumber,
        addressLine1:    data.addressLine1    || f.addressLine1,
        addressLine2:    data.addressLine2    || f.addressLine2,
        city:            data.city            || f.city,
        postcode:        data.postcode        || f.postcode,
        country:         data.country         || f.country,
      }));

      const docLabel = {
        passport:         "Passport",
        driving_licence:  "Driving Licence",
        national_id:      "National ID",
        residence_permit: "Residence Permit",
        other:            "ID Document",
      }[data.documentType] || "ID Document";

      setExtracted({
        label:      docLabel,
        number:     data.documentNumber || null,
        expiry:     data.expiryDate     || null,
        confidence: data.confidence     || null,
      });
    } catch (e) {
      const msg = e.message || "Unknown error";
      const isQuota = msg.includes("quota") || msg.includes("429") || msg.includes("billing");
      setError(
        isQuota
          ? "Gemini API quota exceeded. Enable billing at ai.google.dev or wait for your quota to reset."
          : `ID extraction failed: ${msg}`
      );
    } finally {
      setExtracting(false);
    }
  }

  async function handleSave() {
    if (!form.fullLegalName.trim()) { setError("Full legal name is required."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave({
        legalName: form.fullLegalName.trim(),
        personalProfile: {
          fullLegalName:   form.fullLegalName.trim(),
          preferredName:   form.preferredName.trim(),
          dateOfBirth:     form.dateOfBirth || null,
          niNumber:        form.niNumber.trim(),
          utr:             form.utr.trim(),
          nationality:     form.nationality.trim(),
          residencyStatus: form.residencyStatus,
          addressLine1:    form.addressLine1.trim(),
          addressLine2:    form.addressLine2.trim(),
          city:            form.city.trim(),
          postcode:        form.postcode.trim(),
          country:         form.country.trim(),
          email:           form.email.trim(),
          mobile:          form.mobile.trim(),
          landline:        form.landline.trim(),
        },
        taxFinancial: { employmentType: form.employmentType },
        assignedAccountantUid: form.assignedAccountantUid || null,
        accessLevel:           form.accessLevel,
        onboardingStatus:      form.onboardingStatus,
        // jurisdiction is required by the backend schema; default to UK for individuals
        jurisdiction: "UK",
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to create client.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_SX }}>
      <DialogTitle sx={{ color: TC.primary, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <PersonIcon sx={{ color: TC.linkHover }} />
        Add Individual Client
      </DialogTitle>
      <Divider sx={{ bordercolor: "#0a1628" }} />
      <DialogContent sx={{ pt: 2 }}>

        {/* ── ID Document scan banner ── */}
        <VuiBox
          mb={2}
          p={1.5}
          sx={{
            background: "rgba(0,117,255,0.08)",
            border: "1px dashed rgba(0,117,255,0.4)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            style={{ display: "none" }}
            onChange={handleIdUpload}
          />
          <VuiButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={extracting ? <CircularProgress size={14} color="inherit" /> : <DocumentScannerIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={extracting}
            sx={{ whiteSpace: "nowrap" }}
          >
            {extracting ? "Scanning…" : "Scan ID Document"}
          </VuiButton>

          {extracted ? (
            <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                <strong style={{ color: "#4caf50" }}>Fields auto-filled</strong> from{" "}
                {extracted.label}
                {extracted.number && ` · ${extracted.number}`}
                {extracted.expiry && ` · Expires ${extracted.expiry}`}
                {extracted.confidence != null && ` · ${Math.round(extracted.confidence * 100)}% confidence`}
              </VuiTypography>
            </VuiBox>
          ) : (
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              Upload a passport, driving licence or national ID to auto-fill the form
            </VuiTypography>
          )}
        </VuiBox>

        {error && (
          <VuiBox mb={2} p={1.5} sx={{ background: "rgba(244,67,54,0.12)", border: "1px solid rgba(244,67,54,0.3)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">{error}</VuiTypography>
          </VuiBox>
        )}

        {/* Personal Details */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Personal Details
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Full Legal Name *</FieldLabel>
            <VuiInput
              placeholder="e.g. John James Smith"
              value={form.fullLegalName}
              onChange={(e) => set("fullLegalName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Preferred Name</FieldLabel>
            <VuiInput
              placeholder="e.g. John"
              value={form.preferredName}
              onChange={(e) => set("preferredName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Date of Birth</FieldLabel>
            <VuiInput
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => set("dateOfBirth", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Nationality</FieldLabel>
            <VuiInput
              placeholder="e.g. British"
              value={form.nationality}
              onChange={(e) => set("nationality", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Residency Status</FieldLabel>
            <StyledSelect value={form.residencyStatus} onChange={(e) => set("residencyStatus", e.target.value)}>
              {RESIDENCY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Employment Type</FieldLabel>
            <StyledSelect value={form.employmentType} onChange={(e) => set("employmentType", e.target.value)}>
              {EMPLOYMENT_TYPES.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
        </Grid>

        <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />

        {/* Tax References */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Tax References
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>National Insurance Number</FieldLabel>
            <VuiInput
              placeholder="AB 12 34 56 C"
              value={form.niNumber}
              onChange={(e) => set("niNumber", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>UTR (Unique Taxpayer Reference)</FieldLabel>
            <VuiInput
              placeholder="10-digit UTR"
              value={form.utr}
              onChange={(e) => set("utr", e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />

        {/* Contact */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Contact
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Email</FieldLabel>
            <VuiInput
              type="email"
              placeholder="john.smith@example.com"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Mobile</FieldLabel>
            <VuiInput
              placeholder="+44 7700 000000"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel>Address Line 1</FieldLabel>
            <VuiInput
              placeholder="123 High Street"
              value={form.addressLine1}
              onChange={(e) => set("addressLine1", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>City</FieldLabel>
            <VuiInput
              placeholder="London"
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Postcode</FieldLabel>
            <VuiInput
              placeholder="SW1A 1AA"
              value={form.postcode}
              onChange={(e) => set("postcode", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Country</FieldLabel>
            <VuiInput
              value={form.country}
              onChange={(e) => set("country", e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />

        {/* Relationship */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary,  textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Assigned Accountant & Status
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Assigned Accountant</FieldLabel>
            <StyledSelect value={form.assignedAccountantUid} onChange={(e) => set("assignedAccountantUid", e.target.value)} placeholder="— Unassigned —">
              {users.map((u) => (
                <MenuItem key={u.uid} value={u.uid} sx={{ fontSize: "0.875rem" }}>
                  {u.displayName || u.email}
                </MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Access Level</FieldLabel>
            <StyledSelect value={form.accessLevel} onChange={(e) => set("accessLevel", e.target.value)}>
              {ACCESS_LEVELS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Onboarding Status</FieldLabel>
            <StyledSelect value={form.onboardingStatus} onChange={(e) => set("onboardingStatus", e.target.value)}>
              {ONBOARDING_STATUS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider sx={{ bordercolor: "#0a1628" }} />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <VuiButton variant="outlined" color="secondary" size="small" onClick={onClose} disabled={saving}>
          Cancel
        </VuiButton>
        <VuiButton variant="contained" color="info" size="small" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Create Client"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Company constants ────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  { value: "ltd",         label: "Limited Company (Ltd)" },
  { value: "llp",         label: "LLP" },
  { value: "sole_trader", label: "Sole Trader" },
  { value: "partnership", label: "Partnership" },
  { value: "other",       label: "Other" },
];

const JURISDICTIONS = [
  { value: "UK",    label: "United Kingdom" },
  { value: "IE",    label: "Ireland" },
  { value: "DE",    label: "Germany" },
  { value: "FR",    label: "France" },
  { value: "NL",    label: "Netherlands" },
  { value: "other", label: "Other" },
];

const EMPTY_COMPANY_FORM = {
  legalName: "", tradingName: "", companyRegNumber: "", businessType: "ltd",
  jurisdiction: "UK", incorporationDate: "",
  contactName: "", contactEmail: "",
  assignedAccountantUid: "", accessLevel: "senior",
  onboardingStatus: "pending",
};

// ─── Create Company Dialog ────────────────────────────────────────────────────

function CreateCompanyDialog({ open, onClose, onSave, tenantId, users = [] }) {
  const [form, setForm]               = useState(EMPTY_COMPANY_FORM);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");
  const [chLookupNumber, setChLookupNumber] = useState("");
  const [chLooking, setChLooking]     = useState(false);
  const [chLookupError, setChLookupError] = useState("");
  const [chPopulated, setChPopulated] = useState(false);
  const [chData, setChData]           = useState(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_COMPANY_FORM);
      setError("");
      setChLookupNumber("");
      setChLookupError("");
      setChPopulated(false);
      setChData(null);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleChLookup() {
    if (!chLookupNumber.trim()) return;
    setChLooking(true);
    setChLookupError("");
    setChPopulated(false);
    setChData(null);
    try {
      const result = await lookupCompaniesHouse(chLookupNumber.trim());
      const { company, directors } = result;
      setChData({ company, directors });
      setForm((prev) => ({
        ...prev,
        legalName:         company.legalName         || prev.legalName,
        companyRegNumber:  company.companyRegNumber   || prev.companyRegNumber,
        businessType:      company.businessType       || prev.businessType,
        jurisdiction:      company.jurisdiction       || prev.jurisdiction,
        incorporationDate: company.incorporationDate  || prev.incorporationDate,
      }));
      setChPopulated(true);
    } catch (err) {
      setChLookupError(err?.message || "Company not found at Companies House.");
    } finally {
      setChLooking(false);
    }
  }

  async function handleSave() {
    if (!form.legalName.trim()) { setError("Legal name is required."); return; }
    if (!form.jurisdiction)     { setError("Jurisdiction is required."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e.message || "Failed to create company.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_SX }}>
      <DialogTitle sx={{ color: TC.primary, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon sx={{ color: TC.linkHover }} />
        Add Company
      </DialogTitle>
      <Divider sx={{ bordercolor: "#0a1628" }} />
      <DialogContent sx={{ pt: 2 }}>

        {/* Companies House lookup */}
        <VuiBox
          mb={2} p={1.5}
          sx={{
            background: "rgba(0,117,255,0.08)",
            border: "1px dashed rgba(0,117,255,0.4)",
            borderRadius: "10px",
          }}
        >
          <VuiTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: TC.primary }}>
            🏢 Auto-populate from Companies House
          </VuiTypography>
          <VuiBox display="flex" gap={1} alignItems="center">
            <VuiInput
              placeholder="Enter Company Registration Number (e.g. 12345678)"
              value={chLookupNumber}
              onChange={(e) => setChLookupNumber(e.target.value)}
              sx={{ flex: 1 }}
              onKeyDown={(e) => e.key === "Enter" && handleChLookup()}
            />
            <VuiButton
              color="info" size="small"
              onClick={handleChLookup}
              disabled={chLooking || !chLookupNumber.trim()}
              sx={{ whiteSpace: "nowrap", minWidth: 110 }}
            >
              {chLooking ? "Looking up…" : "Lookup CH"}
            </VuiButton>
          </VuiBox>
          {chLookupError && (
            <VuiTypography variant="caption" color="error" display="block" mt={0.8}>
              {chLookupError}
            </VuiTypography>
          )}
          {chPopulated && chData && (
            <VuiBox mt={1} display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
              <VuiTypography variant="caption" sx={{ color: "#4caf50", fontWeight: 600 }}>
                Fields auto-filled from Companies House
              </VuiTypography>
              {chData.company?.companyStatus && (
                <Chip
                  label={chData.company.companyStatus.toUpperCase()}
                  size="small"
                  sx={{
                    height: 18, fontSize: "0.6rem", fontWeight: 700,
                    bgcolor: chData.company.companyStatus === "active" ? "rgba(76,175,80,0.2)" : "rgba(244,67,54,0.2)",
                    color:   chData.company.companyStatus === "active" ? "#4caf50" : "#f44336",
                    border:  `1px solid ${chData.company.companyStatus === "active" ? "#4caf50" : "#f44336"}`,
                  }}
                />
              )}
            </VuiBox>
          )}
        </VuiBox>

        {error && (
          <VuiBox mb={2} p={1.5} sx={{ background: "rgba(244,67,54,0.12)", border: "1px solid rgba(244,67,54,0.3)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">{error}</VuiTypography>
          </VuiBox>
        )}

        {/* Company Details */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Company Details
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Legal Business Name *</FieldLabel>
            <VuiInput
              placeholder="e.g. Acme Ltd"
              value={form.legalName}
              onChange={(e) => set("legalName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Trading Name</FieldLabel>
            <VuiInput
              placeholder="Trading name (if different)"
              value={form.tradingName}
              onChange={(e) => set("tradingName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Company Reg Number</FieldLabel>
            <VuiInput
              placeholder="e.g. 12345678"
              value={form.companyRegNumber}
              onChange={(e) => set("companyRegNumber", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Business Type</FieldLabel>
            <StyledSelect value={form.businessType} onChange={(e) => set("businessType", e.target.value)}>
              {BUSINESS_TYPES.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Jurisdiction</FieldLabel>
            <StyledSelect value={form.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)}>
              {JURISDICTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Incorporation Date</FieldLabel>
            <VuiInput
              type="date"
              value={form.incorporationDate}
              onChange={(e) => set("incorporationDate", e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />

        {/* Contact */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Contact
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Contact Name</FieldLabel>
            <VuiInput
              placeholder="Primary contact name"
              value={form.contactName}
              onChange={(e) => set("contactName", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Contact Email</FieldLabel>
            <VuiInput
              type="email"
              placeholder="contact@company.com"
              value={form.contactEmail}
              onChange={(e) => set("contactEmail", e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />

        {/* Relationship */}
        <VuiTypography variant="caption" fontWeight="bold"
          sx={{ color: TC.primary, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
          Assigned Accountant & Status
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Assigned Accountant</FieldLabel>
            <StyledSelect value={form.assignedAccountantUid} onChange={(e) => set("assignedAccountantUid", e.target.value)} placeholder="— Unassigned —">
              {users.map((u) => (
                <MenuItem key={u.uid} value={u.uid} sx={{ fontSize: "0.875rem" }}>
                  {u.displayName || u.email}
                </MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Access Level</FieldLabel>
            <StyledSelect value={form.accessLevel} onChange={(e) => set("accessLevel", e.target.value)}>
              {ACCESS_LEVELS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Onboarding Status</FieldLabel>
            <StyledSelect value={form.onboardingStatus} onChange={(e) => set("onboardingStatus", e.target.value)}>
              {ONBOARDING_STATUS.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>{o.label}</MenuItem>
              ))}
            </StyledSelect>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider sx={{ bordercolor: "#0a1628" }} />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <VuiButton variant="outlined" color="secondary" size="small" onClick={onClose} disabled={saving}>
          Cancel
        </VuiButton>
        <VuiButton variant="contained" color="info" size="small" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Create Company"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function IndividualClients() {
  const { tenantId: activeTenantId, hasAllPermissions } = useAuthz();
  const confirm = useConfirm();
  const history = useHistory();

  const [clients, setClients]   = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [createOpen, setCreateOpen]           = useState(false);
  const [companyCreateOpen, setCompanyCreateOpen] = useState(false);
  const [archiving, setArchiving]   = useState(null);

  const canManage = hasAllPermissions([PERMISSIONS.MANAGE_CLIENT_PROFILES]);

  async function load() {
    if (!activeTenantId) return;
    setLoading(true);
    setError("");
    try {
      const [clientsRes, usersRes] = await Promise.all([
        listIndividualClients({ tenantId: activeTenantId, status: statusFilter || undefined }),
        listTenantUsers({ tenantId: activeTenantId }),
      ]);
      setClients(clientsRes.clients.filter((c) => c.clientType === "individual"));
      setUsers(usersRes.users || []);
    } catch (e) {
      setError(e.message || "Failed to load clients.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [activeTenantId, statusFilter]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.uid] = u.displayName || u.email || u.uid; });
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) => {
      const name = (c.personalProfile?.full_legal_name || c.legalName || "").toLowerCase();
      const email = (c.personalProfile?.email || c.contactEmail || "").toLowerCase();
      const ni = (c.personalProfile?.ni_number || "").toLowerCase();
      return !q || name.includes(q) || email.includes(q) || ni.includes(q);
    });
  }, [clients, search]);

  async function handleCreate(payload) {
    await createIndividualClient(activeTenantId, payload);
    await load();
  }

  async function handleCreateCompany(payload) {
    const saved = await createClientProfile(activeTenantId, payload);
    if (payload.companyRegNumber?.trim()) {
      try { await resyncClientFromCH(activeTenantId, saved.id); } catch (_) { /* silent */ }
    }
    await load();
  }

  async function handleArchive(clientId) {
    const ok = await confirm({
      title: "Archive Client",
      message: "Archive this individual client?\nThey will be hidden from active views.",
      confirmLabel: "Archive",
      variant: "warning",
    });
    if (!ok) return;
    setArchiving(clientId);
    try {
      await archiveIndividualClient(activeTenantId, clientId);
      await load();
    } catch (e) {
      setError(e.message || "Failed to archive client.");
    } finally {
      setArchiving(null);
    }
  }

  const tableRows = filtered.map((c) => {
    const pp = c.personalProfile || {};
    const name = pp.full_legal_name || c.legalName || "—";
    const email = pp.email || c.contactEmail || "—";

    return {
      client: (
        <VuiBox display="flex" alignItems="center" gap={1}>
          <VuiBox
            sx={{
              width: 32, height: 32, borderRadius: "50%",
              background: "linear-gradient(135deg,#0075ff,#00b3d4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ color: TC.primary, fontSize: 16 }} />
          </VuiBox>
          <VuiBox>
            <VuiTypography
              variant="button" fontWeight="medium"
              sx={{ cursor: "pointer", "&:hover": { color: TC.linkHover } }}
              onClick={() => history.push(`/individual-clients/${c.id}`)}
            >
              {name}
            </VuiTypography>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
              {email}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      ),
      ni: (
        <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontFamily: "monospace" }}>
          {maskNI(pp.ni_number)}
        </VuiTypography>
      ),
      status: <StatusBadge status={c.status} />,
      onboarding: <OnboardingChip status={c.onboardingStatus} />,
      accountant: (
        <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
          {userMap[c.assignedAccountantUid] || "—"}
        </VuiTypography>
      ),
      action: (
        <VuiBox display="flex" gap={1}>
          <Tooltip title="View / Edit">
            <VuiButton
              variant="text" color="info" size="small"
              onClick={() => history.push(`/individual-clients/${c.id}`)}
              sx={{ minWidth: 0, px: 1 }}
            >
              View
            </VuiButton>
          </Tooltip>
          {canManage && c.status !== "archived" && (
            <Tooltip title="Archive">
              <span>
                <VuiButton
                  variant="text" color="error" size="small"
                  onClick={() => handleArchive(c.id)}
                  disabled={archiving === c.id}
                  sx={{ minWidth: 0, px: 1 }}
                >
                  Archive
                </VuiButton>
              </span>
            </Tooltip>
          )}
        </VuiBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <VuiBox display="flex" alignItems="center" gap={1.5}>
            <PersonIcon sx={{ color: TC.linkHover, fontSize: 28 }} />
            <VuiBox>
              <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>
                Individual Clients
              </VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                {loading ? "Loading…" : `${filtered.length} of ${clients.length} client${clients.length !== 1 ? "s" : ""}`}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          {canManage && (
            <VuiBox display="flex" gap={1}>
              <VuiButton variant="contained" color="info" size="small" startIcon={<BusinessIcon />} onClick={() => setCompanyCreateOpen(true)}>
                New Company
              </VuiButton>
              <VuiButton variant="contained" color="success" size="small" startIcon={<PersonIcon />} onClick={() => setCreateOpen(true)}>
                New Individual
              </VuiButton>
            </VuiBox>
          )}
        </VuiBox>

        {/* Filters */}
        <VuiBox display="flex" gap={2} flexWrap="wrap" mb={3}>
          <VuiInput
            placeholder="Search by name, email or NI…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ "aria-label": "Search individual clients" }}
            icon={{ component: <SearchIcon />, direction: "left" }}
            sx={{ minWidth: 280, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              sx={{
                color: TC.primary, fontSize: "0.875rem",
                background: "#f4f8ff",
                border: "1px solid #d8e6f7",
                borderRadius: "8px",
                "& .MuiSelect-icon": { color: TC.secondary },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "rgba(15,18,59,0.98)",
                    border: "1px solid #d8e6f7",
                    color: TC.primary,
                    "& .MuiMenuItem-root:hover": { background: "rgba(0,117,255,0.15)" },
                    "& .MuiMenuItem-root.Mui-selected": { background: "rgba(0,117,255,0.25)" },
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: "0.875rem", color: TC.midtone }}>All Statuses</MenuItem>
              <MenuItem value="active"   sx={{ fontSize: "0.875rem" }}>Active</MenuItem>
              <MenuItem value="archived" sx={{ fontSize: "0.875rem" }}>Archived</MenuItem>
            </Select>
          </FormControl>
        </VuiBox>

        {/* Error */}
        {error && (
          <VuiBox mb={2} p={1.5} sx={{ background: "rgba(244,67,54,0.12)", border: "1px solid rgba(244,67,54,0.3)", borderRadius: "8px" }}>
            <VuiTypography variant="caption" color="error">{error}</VuiTypography>
          </VuiBox>
        )}

        {/* Table */}
        <Card>
          {loading ? (
            <VuiBox p={2}>
              <SkeletonTable
                rows={5}
                columns={[
                  { name: "client",     skeleton: "multi" },
                  { name: "ni"         },
                  { name: "status",     skeleton: "badge", align: "center" },
                  { name: "onboarding", skeleton: "badge", align: "center" },
                  { name: "accountant" },
                  { name: "action",     skeleton: "icon",  align: "right" },
                ]}
              />
            </VuiBox>
          ) : filtered.length === 0 ? (
            <VuiBox p={4} textAlign="center">
              <PersonIcon sx={{ color: TC.tertiary, fontSize: 48, mb: 1 }} />
              <VuiTypography variant="body2" sx={{ color: TC.secondary }}>
                {search ? "No clients match your search." : "No individual clients yet. Click 'Add Individual' to get started."}
              </VuiTypography>
            </VuiBox>
          ) : (
            <Table
              columns={[
                { name: "client",     align: "left"  },
                { name: "ni",         align: "left"  },
                { name: "status",     align: "center" },
                { name: "onboarding", align: "center" },
                { name: "accountant", align: "left"  },
                { name: "action",     align: "right" },
              ]}
              rows={tableRows}
            />
          )}
        </Card>
      </VuiBox>

      <Footer />

      <CreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
        tenantId={activeTenantId}
        users={users}
      />

      <CreateCompanyDialog
        open={companyCreateOpen}
        onClose={() => setCompanyCreateOpen(false)}
        onSave={handleCreateCompany}
        tenantId={activeTenantId}
        users={users}
      />
    </DashboardLayout>
  );
}

